import { useMemo } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import { Permission } from '@/api';
import { FilterCheckboxItem } from '@/design_system/Filters/FiltersMenu/FilterCheckboxItem';
import { FiltersMenu, FiltersSubMenu } from '@/design_system/Filters/FiltersMenu/FiltersMenu';
import { FilterTag } from '@/design_system/Filters/FiltersMenu/FilterTag';
import IconTask from '@/icons/Task.svg';

import { PERMISSION_LABELS } from '../../permission-labels';
import { useFilters } from '../FiltersState';

const ALL_PERMISSIONS = Object.keys(PERMISSION_LABELS) as Permission[];

export const FiltersPermission = ({ tag }: { tag?: boolean }) => {
  const { t, i18n } = useLingui();

  const {
    permissions: { permissions, setPermissions },
  } = useFilters();

  const sortedPermissions = useMemo(
    () =>
      [...ALL_PERMISSIONS].sort((a, b) =>
        i18n._(PERMISSION_LABELS[a]).localeCompare(i18n._(PERMISSION_LABELS[b]))
      ),
    [i18n]
  );

  const options = sortedPermissions.map((id) => (
    <FilterCheckboxItem
      key={id}
      checked={permissions.includes(id)}
      onChange={(checked) => {
        if (checked) {
          setPermissions([...permissions, id]);
        } else {
          setPermissions(permissions.filter((p) => p !== id));
        }
      }}
    >
      {i18n._(PERMISSION_LABELS[id])}
    </FilterCheckboxItem>
  ));

  if (tag) {
    if (permissions.length === 0) {
      return null;
    }

    const numberOfPermissions = permissions.length;

    return (
      <FiltersMenu
        button={
          <FilterTag
            label={t({
              id: 'settings.organization.roles.filters.permission.label',
              message: 'Permission',
            })}
            value={
              permissions.length === 1 ? (
                i18n._(PERMISSION_LABELS[permissions[0]])
              ) : (
                <Trans id="settings.organization.roles.filters.permission.multiple">
                  {numberOfPermissions} permissions
                </Trans>
              )
            }
            tooltip={
              permissions.length > 1
                ? permissions.map((p) => i18n._(PERMISSION_LABELS[p])).join(', ')
                : undefined
            }
            onClear={() => setPermissions([])}
          />
        }
      >
        {options}
      </FiltersMenu>
    );
  }

  return (
    <FiltersSubMenu
      icon={<IconTask />}
      label={t({
        id: 'settings.organization.roles.filters.permission.label',
        message: 'Permission',
      })}
    >
      {options}
    </FiltersSubMenu>
  );
};
